import React, { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const MechanicDashboard = ({ user, setUser }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFinishDialog, setShowFinishDialog] = useState(false);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [vehicleDesc, setVehicleDesc] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/mechanic/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setStatus(data);
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();

    // Polling for updates every 5 seconds
    const pollInterval = setInterval(() => {
      fetchStatus();
    }, 5000);

    // Try WebSocket connection (fallback to polling if it fails)
    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws/dashboard`;
      const ws = new WebSocket(wsUrl);

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'status_update') {
          fetchStatus();
        }
      };

      ws.onerror = () => {
        // WebSocket failed, polling will handle updates
      };

      return () => {
        clearInterval(pollInterval);
        ws.close();
      };
    } catch (error) {
      // WebSocket not supported or failed, use polling only
      return () => clearInterval(pollInterval);
    }
  }, [fetchStatus]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFinishVehicle = async () => {
    if (!vehicleDesc || !photo) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/mechanic/finish-vehicle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          descripcion_vehiculo: vehicleDesc,
          foto_base64: photoPreview
        })
      });

      if (response.ok) {
        toast.success('¡Vehículo marcado como terminado!');
        setShowFinishDialog(false);
        setVehicleDesc('');
        setPhoto(null);
        setPhotoPreview(null);
        fetchStatus();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al marcar vehículo');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartVehicle = async () => {
    if (!vehicleDesc) {
      toast.error('Por favor ingresa la descripción del vehículo');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/mechanic/start-vehicle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          descripcion_vehiculo: vehicleDesc
        })
      });

      if (response.ok) {
        toast.success('¡Vehículo iniciado!');
        setShowStartDialog(false);
        setVehicleDesc('');
        fetchStatus();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al iniciar vehículo');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-red-600 text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="bg-red-600 p-3 rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Servicio Melgoza</h1>
                <p className="text-sm text-gray-400">{user.nombre}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-zinc-700 text-gray-300 hover:bg-zinc-800"
              data-testid="logout-button"
            >
              Salir
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-zinc-900 rounded-3xl p-8 border border-zinc-800 shadow-2xl">
          {/* Status Badge */}
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-white">Mi Estado</h2>
            <div className={`px-6 py-3 rounded-full font-bold text-lg ${
              status?.status === 'LIBRE' ? 'bg-green-600/20 text-green-400 border-2 border-green-600' : 'bg-red-600/20 text-red-400 border-2 border-red-600'
            }`} data-testid="mechanic-status-badge">
              {status?.status === 'LIBRE' ? '🟢 LIBRE' : '🔴 OCUPADO'}
            </div>
          </div>

          {/* OCUPADO State */}
          {status?.status === 'OCUPADO' && (
            <div className="space-y-6">
              <div className="bg-zinc-800 p-6 rounded-2xl border border-zinc-700">
                <p className="text-gray-400 text-sm mb-2">Trabajando en:</p>
                <p className="text-white text-2xl font-semibold" data-testid="current-vehicle">{status.vehiculo_actual || 'Sin vehículo asignado'}</p>
              </div>

              <Button
                onClick={() => {
                  setVehicleDesc(status.vehiculo_actual || '');
                  setShowFinishDialog(true);
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-8 text-xl rounded-2xl transition-all duration-200 shadow-lg shadow-green-900/50 hover:shadow-green-900/70"
                data-testid="finish-vehicle-button"
              >
                ✓ Marcar Vehículo como TERMINADO
              </Button>
            </div>
          )}

          {/* LIBRE State */}
          {status?.status === 'LIBRE' && (
            <div className="space-y-6">
              {/* Position in Queue */}
              <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 border-2 border-green-600 p-8 rounded-2xl text-center">
                <p className="text-green-400 text-lg mb-2">Tu posición en la fila:</p>
                <p className="text-green-300 text-6xl font-bold" data-testid="queue-position">#{status.position || '-'}</p>
              </div>

              {/* Last Vehicle Info */}
              {status.ultimo_terminado_at && (
                <div className="bg-zinc-800 p-6 rounded-2xl border border-zinc-700">
                  <p className="text-gray-400 text-sm mb-2">Último vehículo terminado:</p>
                  <p className="text-white text-lg mb-4">
                    {formatDistanceToNow(new Date(status.ultimo_terminado_at), { addSuffix: true, locale: es })}
                  </p>
                  {status.foto_ultimo_terminado_url && (
                    <img
                      src={status.foto_ultimo_terminado_url}
                      alt="Último vehículo"
                      className="w-full h-48 object-cover rounded-xl border border-zinc-700"
                      data-testid="last-vehicle-photo"
                    />
                  )}
                </div>
              )}

              <Button
                onClick={() => setShowStartDialog(true)}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-8 text-xl rounded-2xl transition-all duration-200 shadow-lg shadow-red-900/50 hover:shadow-red-900/70"
                data-testid="start-vehicle-button"
              >
                🚗 Iniciar Nuevo Vehículo
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Finish Vehicle Dialog */}
      <Dialog open={showFinishDialog} onOpenChange={setShowFinishDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Marcar Vehículo como Terminado</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="vehicle-desc">Descripción del vehículo</Label>
              <Textarea
                id="vehicle-desc"
                value={vehicleDesc}
                onChange={(e) => setVehicleDesc(e.target.value)}
                placeholder="Ej: Nissan Sentra 2018 Blanco ABC-123"
                className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
                data-testid="finish-vehicle-description"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="photo">Foto de evidencia (obligatoria)</Label>
              <Input
                id="photo"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="bg-zinc-800 border-zinc-700 text-white"
                data-testid="finish-vehicle-photo-input"
              />
              {photoPreview && (
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="w-full h-64 object-cover rounded-xl border border-zinc-700 mt-4"
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowFinishDialog(false)}
              variant="outline"
              className="border-zinc-700 text-gray-300"
              data-testid="cancel-finish-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleFinishVehicle}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="confirm-finish-button"
            >
              {submitting ? 'Guardando...' : 'Marcar Terminado'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Vehicle Dialog */}
      <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Iniciar Nuevo Vehículo</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-vehicle-desc">Descripción del vehículo</Label>
              <Textarea
                id="new-vehicle-desc"
                value={vehicleDesc}
                onChange={(e) => setVehicleDesc(e.target.value)}
                placeholder="Ej: Toyota Corolla 2020 Rojo XYZ-789"
                className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
                data-testid="start-vehicle-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowStartDialog(false)}
              variant="outline"
              className="border-zinc-700 text-gray-300"
              data-testid="cancel-start-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleStartVehicle}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-start-button"
            >
              {submitting ? 'Guardando...' : 'Iniciar Vehículo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MechanicDashboard;
